import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Heart, CircleDollarSign, ThumbsUp, Share2, Bookmark, Rocket, Flame, ArrowUpRight, Inbox, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PitchFlipCard from "@/components/PitchFlipCard";

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-5 flex items-center gap-4">
    <div className="w-11 h-11 rounded-xl bg-[#F5B301]/10 border border-[#F5B301]/20 flex items-center justify-center">
      <Icon size={19} className={color} />
    </div>
    <div className="text-left">
      <p className="font-heading font-bold text-2xl text-white">{value}</p>
      <p className="text-xs text-zinc-500">{label}</p>
    </div>
  </div>
);

const InterestInbox = () => {
  const [interests, setInterests] = useState(null);
  useEffect(() => { api.get("/my-interests").then((r) => setInterests(r.data)).catch(() => setInterests([])); }, []);
  if (!interests) return null;
  return (
    <div className="mt-12" data-testid="interest-inbox">
      <h2 className="text-xl font-semibold text-left mb-5 flex items-center gap-2">
        <Inbox size={18} className="text-[#F5B301]" /> Interest Inbox ({interests.length})
      </h2>
      {interests.length === 0 ? (
        <div className="rounded-2xl bg-[#0d0d0d] border border-dashed border-white/15 p-10 text-center" data-testid="interest-inbox-empty">
          <Inbox size={28} className="text-[#F5B301] mx-auto mb-3" />
          <p className="text-white font-semibold">No capital interest yet</p>
          <p className="text-sm text-zinc-500 mt-1">When investors express interest in your pitches, they'll show up here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="interest-inbox-list">
          {interests.map((it, idx) => (
            <div key={`${it.pitch_id}-${it.investor_email}-${idx}`} className="rounded-xl bg-[#0d0d0d] border border-white/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 text-left gold-glow-hover">
              <span className="w-9 h-9 rounded-full bg-[#F5B301] text-black text-sm font-bold flex items-center justify-center shrink-0">
                {it.investor_name?.[0]?.toUpperCase()}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                  {it.investor_name}
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#F5B301]">{it.investor_role}</span>
                </p>
                <p className="text-xs text-zinc-500 truncate flex items-center gap-1.5"><Mail size={11} /> {it.investor_email}</p>
              </div>
              <div className="text-left sm:text-right shrink-0">
                <p className="text-xs text-zinc-300">Interested in <span className="text-[#F5B301] font-semibold">{it.company}</span></p>
                <p className="text-[11px] text-zinc-600">{new Date(it.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FounderDashboard = ({ user }) => {
  const [pitches, setPitches] = useState(null);
  useEffect(() => { api.get("/my-pitches").then((r) => setPitches(r.data)); }, []);
  if (!pitches) return <p className="text-zinc-500 text-sm">Loading your pitches…</p>;
  const total = (k) => pitches.reduce((s, p) => s + p[k], 0);
  return (
    <div data-testid="founder-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={Heart} label="Total Hearts" value={total("hearts")} color="text-red-500" />
        <StatCard icon={CircleDollarSign} label="Investor Interest" value={total("dollars")} color="text-[#F5B301]" />
        <StatCard icon={ThumbsUp} label="Total Likes" value={total("likes")} color="text-blue-400" />
        <StatCard icon={Share2} label="Total Shares" value={total("shares")} color="text-zinc-300" />
      </div>
      <h2 className="text-xl font-semibold text-left mb-5">My Pitches ({pitches.length})</h2>
      {pitches.length === 0 ? (
        <div className="rounded-2xl bg-[#0d0d0d] border border-dashed border-white/15 p-12 text-center" data-testid="founder-empty-state">
          <Rocket size={32} className="text-[#F5B301] mx-auto mb-4" />
          <p className="text-white font-semibold">No pitches yet</p>
          <p className="text-sm text-zinc-500 mt-1">Pitch upload is coming soon — your dashboard is ready for it.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="founder-pitch-list">
          {pitches.map((p) => (
            <div key={p.id} className="rounded-2xl bg-[#0d0d0d] border border-white/5 p-5 flex flex-col sm:flex-row sm:items-center gap-5 text-left gold-glow-hover">
              <img src={p.thumbnail} alt={p.company} className="w-full sm:w-24 h-32 sm:h-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <Link to={`/pitch/${p.id}`} className="font-heading font-semibold text-white flex items-center gap-2 hover:text-[#F5B301] transition-colors">
                  {p.company} <ArrowUpRight size={13} className="text-zinc-500" />
                  {p.trending && (
                    <span className="flex items-center gap-1 text-[10px] font-bold text-black px-2 py-0.5 rounded-full bg-gradient-to-r from-[#F5B301] to-orange-500 trending-badge">
                      <Flame size={10} fill="black" /> Trending
                    </span>
                  )}
                </Link>
                <p className="text-xs text-zinc-500">{p.tagline} · Raising {p.deck.ask}</p>
              </div>
              <div className="flex gap-5 text-xs text-zinc-300 shrink-0">
                <span className="flex items-center gap-1.5"><Heart size={13} className="text-red-500" /> {p.hearts}</span>
                <span className="flex items-center gap-1.5"><CircleDollarSign size={13} className="text-[#F5B301]" /> {p.dollars}</span>
                <span className="flex items-center gap-1.5"><ThumbsUp size={13} className="text-blue-400" /> {p.likes}</span>
                <span className="flex items-center gap-1.5"><Share2 size={13} /> {p.shares}</span>
              </div>
              <div className="text-right shrink-0">
                <p className="font-heading font-bold text-xl text-[#F5B301]">{p.score}</p>
                <p className="text-[10px] uppercase tracking-widest text-zinc-600">EBN Score</p>
              </div>
            </div>
          ))}
        </div>
      )}
      <InterestInbox />
    </div>
  );
};

const InvestorDashboard = () => {
  const { savedIds } = useAuth();
  const [pitches, setPitches] = useState(null);
  useEffect(() => { api.get("/watchlist").then((r) => setPitches(r.data)); }, [savedIds]);
  if (!pitches) return <p className="text-zinc-500 text-sm">Loading your watchlist…</p>;
  return (
    <div data-testid="investor-dashboard">
      <h2 className="text-xl font-semibold text-left mb-5 flex items-center gap-2">
        <Bookmark size={18} className="text-[#F5B301]" /> My Watchlist ({pitches.length})
      </h2>
      {pitches.length === 0 ? (
        <div className="rounded-2xl bg-[#0d0d0d] border border-dashed border-white/15 p-12 text-center" data-testid="watchlist-empty-state">
          <Bookmark size={32} className="text-[#F5B301] mx-auto mb-4" />
          <p className="text-white font-semibold">Your watchlist is empty</p>
          <p className="text-sm text-zinc-500 mt-1">Tap the bookmark on any pitch to save it here.</p>
          <Link to="/pitches" data-testid="watchlist-browse-btn" className="inline-block mt-5 px-6 py-2.5 rounded-full bg-[#F5B301] text-black text-sm font-bold hover:bg-[#D99E01] transition-colors">
            Browse Pitches
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10" data-testid="watchlist-grid">
          {pitches.map((p) => <PitchFlipCard key={p.id} pitch={p} />)}
        </div>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  if (user === null) return <main className="pt-32 text-center text-zinc-500 text-sm min-h-screen">Checking session…</main>;
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <main className="relative pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 min-h-screen">
      <div className="noise-overlay" />
      <div className="text-left mb-10">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Hey, <span className="gold-gradient-text">{user.name.split(" ")[0]}</span>
        </h1>
        <p className="mt-2 text-sm text-zinc-500 capitalize" data-testid="dashboard-role">
          {user.role} account · {user.email}
        </p>
      </div>
      {user.role === "founder" ? <FounderDashboard user={user} /> : <InvestorDashboard />}
    </main>
  );
}
